import React from 'react';
import {
	ExperienceSuccess,
	ExperienceErrorBoundary,
} from '@atlassian/jira-business-experience-tracking';
import { fg } from '@atlassian/jira-feature-gating';
import type { IssueId } from '@atlassian/jira-shared-types';
import { RoadmapDependencyFlyout } from '@atlassian/jira-software-roadmap-timeline-table-kit';
import type {
	OnUnlinkCallback,
	ChartTimelineOverlayRendererProps,
} from '@atlassian/jira-software-roadmap-timeline-table-kit/src/types';
import { VIEW_DEPENDENCY_EXPERIENCE } from '../../../../common/constants';
import type { TimelineViewIssue } from '../../../../services/get-timeline-view-issues/types';
import { getDepsItem, getIssueFromMap, getOverlayIssueItem } from './utils';

type Props = ChartTimelineOverlayRendererProps & {
	issueId?: IssueId;
	issuesMap: Map<string, TimelineViewIssue>;
	canUnlink: boolean;
	dependees: IssueId[];
	dependencies: IssueId[];
	onUnlink: OnUnlinkCallback;
	onCloseTooltip: () => void;
};

const DependencyFlyoutComponent = ({
	id,
	position,
	issueId,
	issuesMap,
	dependees,
	dependencies,
	canUnlink,
	onUnlink,
	onCloseTooltip,
}: Props) => {
	const fromIssue = issueId ? getIssueFromMap(issueId, issuesMap) : undefined;
	const toIssue = getIssueFromMap(Number(dependees[0]), issuesMap); // there's only one "to" issue which clicked dependency can link to
	// searching for the same dependency in the "from" and "to" issues by ID to determine which one was clicked
	const dependency = (fromIssue?.issueLinks || []).find((fromLink) =>
		(toIssue?.issueLinks || []).map((toLink) => toLink.issueLinkId).includes(fromLink.issueLinkId),
	);
	const item = getOverlayIssueItem(fromIssue);

	if (!item) {
		return null;
	}

	return (
		<>
			<RoadmapDependencyFlyout
				id={id}
				item={item}
				position={position}
				// @ts-expect-error - TS2322 - Type '(OverlayItem | undefined)[]' is not assignable to type 'OverlayItem[]'.
				dependencies={getDepsItem({ issuesMap, issueIds: dependencies })}
				// @ts-expect-error - TS2322 - Type '(OverlayItem | undefined)[]' is not assignable to type 'OverlayItem[]'.
				dependees={getDepsItem({ issuesMap, issueIds: dependees })}
				implicitDependencyItems={[]}
				maybeInwardDependencyDescription={dependency?.type.inward}
				maybeOutwardDependencyDescription={dependency?.type.outward}
				onClose={onCloseTooltip}
				{...(canUnlink ? { onUnlink } : undefined)}
			/>
			{!fg('remove_business_timeline_viewdependency_experience') && (
				<ExperienceSuccess experience={VIEW_DEPENDENCY_EXPERIENCE} />
			)}
		</>
	);
};

export const DependencyFlyout = (props: Props) => {
	if (fg('remove_business_timeline_viewdependency_experience')) {
		return (
			<ExperienceErrorBoundary experience={VIEW_DEPENDENCY_EXPERIENCE} shouldStartExperience>
				<DependencyFlyoutComponent {...props} />
			</ExperienceErrorBoundary>
		);
	}

	return <DependencyFlyoutComponent {...props} />;
};
